import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'scaleflow/services/store';
import { HOST } from 'scaleflow/adapters/application';
import { DateTime } from 'scaleflow/utils/date-time';
import { restartableTask, timeout } from 'ember-concurrency';
import { DEFAULT_AI_MODEL } from 'scaleflow/utils/ai-models';
import { formatBytes } from 'scaleflow/utils/manual-repo-archives';

type ConnectorId = 'github' | 'github-public' | 'gitlab' | 'manual';

type Repo = {
  name: string;
  fullName: string;
  cloneUrl: string;
  isPrivate: boolean;
  updatedAt: string;
  size: string;
};

/**
 * GitHub reports repository size in KB, not bytes. Anything under a megabyte
 * stays whole ("640 KB"); above that `formatBytes` picks MB/GB and adds the
 * decimal.
 */
function formatRepoSize(sizeKb: number | null | undefined): string {
  if (sizeKb == null) return '\u2014';
  if (sizeKb < 1024) return `${Math.round(sizeKb)} KB`;
  return formatBytes(sizeKb * 1024);
}

function formatUpdatedAt(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

type RepoResource = {
  attributes: {
    name: string;
    full_name: string;
    clone_url: string;
    private: boolean;
    updated_at: string;
    // GitHub-only — the GitLab repos endpoint doesn't report a size, so this
    // stays optional and `formatRepoSize` falls back to '—' when it's absent.
    size_kb?: number | null;
  };
};

type RepoPayload = {
  data?: RepoResource[];
  meta?: {
    total_count?: number;
    credential_status?: string;
    repos_status?: string;
  };
};

/**
 * Every repo endpoint — GitHub authenticated, GitHub public, GitLab — returns
 * the same JSON:API resource shape, so they all normalize the same way.
 */
function mapRepos(json: RepoPayload): Repo[] {
  return (json.data ?? []).map((item) => ({
    name: item.attributes.name,
    fullName: item.attributes.full_name,
    cloneUrl: item.attributes.clone_url,
    isPrivate: item.attributes.private,
    updatedAt: formatUpdatedAt(item.attributes.updated_at),
    // GitLab's own payload never carries this — formatRepoSize(undefined)
    // falls back to '—', which is the only caller left on this helper.
    size: formatRepoSize(item.attributes.size_kb),
  }));
}

export default class ScansNewController extends Controller {
  @service declare router: RouterService;
  @service declare store: StoreService;

  declare model: any;

  @tracked currentStep = 1;
  @tracked connectorType: ConnectorId | null = null;
  @tracked slackChannelId = '';

  /**
   * The connected GitHub integration record, if any. Its `hasRepositoryCount`
   * and `repositorySelectionKey` getters decide what the repo-list header shows.
   */
  get githubIntegration() {
    return [...(this.model?.integrations ?? [])].find(
      (i: { provider_name: string }) => i.provider_name === 'github',
    );
  }

  get availableConnectors() {
    const model = this.model;
    const integrations: { provider_name: string; provider_id: string }[] = [
      ...(model?.integrations ?? []),
    ];
    const connectedIds = new Set(
      integrations
        .filter((i) => i.provider_id !== 'pending')
        .map((i) => i.provider_name),
    );
    return (model?.connectors ?? []).filter(
      (c: { id: string; enabled: boolean; requiresIntegration?: boolean }) =>
        c.enabled &&
        (c.requiresIntegration === false || connectedIds.has(c.id)),
    );
  }
  @tracked selectedRepos = new Set<string>();
  @tracked isStarting = false;

  // GitHub (authenticated) flow
  @tracked githubRepos: Repo[] = [];
  @tracked isFetchingGithubRepos = false;

  // GitLab flow — connected with a pasted token rather than an OAuth install,
  // but from here on identical to the GitHub authenticated flow.
  @tracked gitlabRepos: Repo[] = [];
  @tracked isFetchingGitlabRepos = false;
  /** True once the retry budget below was spent while still `FETCHING`. */
  @tracked gitlabReposTimedOut = false;
  /** Set when the list job itself reports `FAILED` — not a retry case. */
  @tracked gitlabReposError: string | null = null;

  /**
   * Wait, in seconds, before each retry while `/integrations/gitlab/repos`
   * keeps reporting `repos_status: 'FETCHING'`. `n` starts at 2 and steps by
   * 2 each retry (2, 4, 6, …, 20); the wait is `n + 2`. A class field, not an
   * inline constant, so a test can collapse it to `[0]` instead of actually
   * waiting minutes.
   */
  gitlabRetryWaitsSeconds = Array.from(
    { length: 10 },
    (_, i) => (i + 1) * 2 + 2,
  );

  // Repo search (shared across both flows)
  @tracked repoSearch = '';

  // Public repos flow
  @tracked orgName = '';
  @tracked publicRepos: Repo[] = [];
  @tracked isFetchingPublicRepos = false;
  @tracked publicFetchError: string | null = null;
  @tracked hasFetched = false;

  /**
   * Exhaustive on purpose. A default case falling through to `githubRepos`
   * would silently hand the GitLab step GitHub's repo list — wrong data with no
   * error anywhere. See docs/ai-blueprints/gitlab-connector-frontend.md §9.
   */
  get repos(): Repo[] {
    switch (this.connectorType) {
      case 'github':
        return this.githubRepos;
      case 'github-public':
        return this.publicRepos;
      case 'gitlab':
        return this.gitlabRepos;
      case 'manual':
      case null:
        // Neither path has a repo list of its own.
        return [];
    }
  }

  /** True while whichever connector's repo list is being fetched. */
  get isFetchingRepos(): boolean {
    return this.isFetchingGithubRepos || this.isFetchingGitlabRepos;
  }

  /** Display name for the connector the step-2 repo list came from. */
  get repoSourceName(): string {
    return this.connectorType === 'gitlab' ? 'GitLab' : 'GitHub';
  }

  get stepSubtitle(): string {
    if (this.currentStep === 1) {
      return 'Choose a connector to scan repositories from';
    }
    if (this.currentStep === 3) {
      return 'Set up Slack notifications for this scan (optional)';
    }
    if (this.connectorType === 'github-public') {
      return 'Browse public repositories from a GitHub organization';
    }
    if (this.connectorType === 'gitlab') {
      return 'Select repositories from GitLab';
    }
    return 'Select repositories from GitHub';
  }

  get selectedCount(): number {
    return this.selectedRepos.size;
  }

  get canStart(): boolean {
    if (this.connectorType === 'manual') return true;
    return this.selectedRepos.size > 0;
  }

  get allSelected(): boolean {
    return (
      this.repos.length > 0 && this.selectedRepos.size === this.repos.length
    );
  }

  @action
  goToStep1() {
    this.currentStep = 1;
    this.connectorType = null;
    this.selectedRepos = new Set();
    this.githubRepos = [];
    this.isFetchingGithubRepos = false;
    this.gitlabRepos = [];
    this.isFetchingGitlabRepos = false;
    this.gitlabReposTimedOut = false;
    this.gitlabReposError = null;
    void this.fetchGitlabRepos.cancelAll();
    this.repoSearch = '';
    this.orgName = '';
    this.publicRepos = [];
    this.isFetchingPublicRepos = false;
    this.publicFetchError = null;
    this.hasFetched = false;
    this.slackChannelId = '';
  }

  @action
  goToStep2() {
    this.currentStep = 2;
    this.slackChannelId = '';
  }

  @action
  goToStep3() {
    this.currentStep = 3;
  }

  /**
   * The step-3 "2 Repositories" pill doubles as a back-button. The manual
   * path never visits step 2 (there's no repo list for it), so going back
   * from there lands on step 1 instead — step 2's default view would
   * otherwise show an empty GitHub repo list that was never fetched.
   */
  @action
  goBackFromNotifications() {
    if (this.connectorType === 'manual') {
      this.goToStep1();
    } else {
      this.goToStep2();
    }
  }

  @action
  updateSlackChannelId(event: Event) {
    this.slackChannelId = (event.target as HTMLInputElement).value;
  }

  @action
  async selectConnector(connectorId: string) {
    this.connectorType = connectorId as ConnectorId;

    // No repo list applies to manual upload — skip straight to Notifications
    // rather than land on a step 2 with nothing to show.
    if (connectorId === 'manual') {
      this.currentStep = 3;
      return;
    }

    this.currentStep = 2;

    if (connectorId === 'github') {
      await this.fetchGithubRepos();
    } else if (connectorId === 'gitlab') {
      await this.fetchGitlabRepos.perform();
    }
    // 'github-public' fetches on demand once an org name is entered.
  }

  async fetchGithubRepos() {
    this.isFetchingGithubRepos = true;
    try {
      const adapter = this.store.adapterFor('application') as any;
      const json = await adapter.ajax(
        `${HOST}/integrations/github/repos`,
        'GET',
      );
      this.githubRepos = (json.data ?? []).map(
        (item: {
          attributes: {
            name: string;
            full_name: string;
            clone_url: string;
            private: boolean;
            updated_at: string;
            size_kb: number | null;
          };
        }) => ({
          name: item.attributes.name,
          fullName: item.attributes.full_name,
          cloneUrl: item.attributes.clone_url,
          isPrivate: item.attributes.private,
          updatedAt: formatUpdatedAt(item.attributes.updated_at),
          size: formatRepoSize(item.attributes.size_kb),
        }),
      );
    } catch {
      this.githubRepos = [];
    } finally {
      this.isFetchingGithubRepos = false;
    }

    await this.reloadIntegrationSettings();
  }

  /**
   * `repository_count` and `repository_selection` are written by the GitHub App
   * install callback, so the record loaded in the route's model hook can predate
   * them. Refresh once the repo list is in — the store updates the existing
   * records in place, so `githubIntegration` picks the new values up. A failure
   * here only costs a stale count, never the repo list, so it is swallowed.
   */
  async reloadIntegrationSettings() {
    try {
      await this.store.findAll('integration-setting', { reload: true });
    } catch {
      // Nothing to recover: the header segments simply stay hidden or stale.
    }
  }

  /**
   * `/integrations/gitlab/repos` returns a cached list plus `meta.repos_status`
   * rather than fetching live (see docs/ai-blueprints/gitlab-connector-frontend.md
   * §5). While it reports `FETCHING`, the async list job is still running —
   * this pings the same endpoint again rather than treating an empty `data`
   * as "no repos". Stops on `READY`, `FAILED`, or the retry budget running out.
   *
   * `restartableTask` so a second `fetchGitlabRepos()` call (re-selecting the
   * GitLab step) cancels an in-flight retry loop rather than running two.
   */
  fetchGitlabRepos = restartableTask(async () => {
    this.isFetchingGitlabRepos = true;
    this.gitlabReposTimedOut = false;
    this.gitlabReposError = null;

    try {
      for (let attempt = 0; ; attempt++) {
        let json: RepoPayload;
        try {
          const adapter = this.store.adapterFor('application') as any;
          json = (await adapter.ajax(
            `${HOST}/integrations/gitlab/repos`,
            'GET',
          )) as RepoPayload;
        } catch {
          this.gitlabRepos = [];
          return;
        }

        const status = json.meta?.repos_status;

        if (status === 'FAILED') {
          this.gitlabRepos = [];
          this.gitlabReposError = 'GitLab could not list your repositories.';
          return;
        }

        if (status !== 'FETCHING') {
          this.gitlabRepos = mapRepos(json);
          return;
        }

        const waitSeconds = this.gitlabRetryWaitsSeconds[attempt];
        if (waitSeconds === undefined) {
          // Retry budget spent and the job is still running. The record
          // carries the answer whenever it lands — reopening this step
          // re-enters this same task rather than polling forever.
          this.gitlabRepos = [];
          this.gitlabReposTimedOut = true;
          return;
        }

        await timeout(waitSeconds * 1000);
      }
    } finally {
      this.isFetchingGitlabRepos = false;
    }
  });

  @action
  updateOrgName(event: Event) {
    this.orgName = (event.target as HTMLInputElement).value;
  }

  @action
  handleOrgNameKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      void this.fetchPublicRepos();
    }
  }

  @action
  async fetchPublicRepos() {
    const org = this.orgName.trim();
    if (!org) return;

    this.isFetchingPublicRepos = true;
    this.publicFetchError = null;
    this.hasFetched = false;

    try {
      const adapter = this.store.adapterFor('application') as any;
      const json = await adapter.ajax(
        `${HOST}/integrations/github/repos?org=${encodeURIComponent(org)}`,
        'GET',
      );
      this.publicRepos = (json.data ?? []).map(
        (item: {
          attributes: {
            name: string;
            full_name: string;
            clone_url: string;
            private: boolean;
            updated_at: string;
            size_kb: number | null;
          };
        }) => ({
          name: item.attributes.name,
          fullName: item.attributes.full_name,
          cloneUrl: item.attributes.clone_url,
          isPrivate: item.attributes.private,
          updatedAt: formatUpdatedAt(item.attributes.updated_at),
          size: formatRepoSize(item.attributes.size_kb),
        }),
      );
      this.hasFetched = true;
    } catch {
      this.publicFetchError =
        'Could not fetch repositories. Check the organization name and try again.';
      this.publicRepos = [];
      this.hasFetched = true;
    } finally {
      this.isFetchingPublicRepos = false;
    }
  }

  @action
  toggleRepo(repoName: string) {
    const updated = new Set(this.selectedRepos);
    if (updated.has(repoName)) {
      updated.delete(repoName);
    } else {
      updated.add(repoName);
    }
    this.selectedRepos = updated;
  }

  @action
  isSelected(repoName: string): boolean {
    return this.selectedRepos.has(repoName);
  }

  @action
  toggleAll() {
    if (this.allSelected) {
      this.selectedRepos = new Set();
    } else {
      this.selectedRepos = new Set(this.repos.map((r) => r.fullName));
    }
  }

  @action
  toggleRepoFromEvent(repoName: string) {
    this.toggleRepo(repoName);
  }

  @action
  async startScan() {
    if (!this.canStart || this.isStarting) return;
    this.isStarting = true;

    const scan = this.model?.scan;
    if (!scan) {
      this.isStarting = false;
      return;
    }
    scan.title = `Xray - ${DateTime.now().format('ddd, Do MMMM, YYYY')}`;
    scan.status = 'IN_PROGRESS';
    scan.ai_model = DEFAULT_AI_MODEL;
    scan.connector = this.connectorType;
    scan.slack_channel_id = this.slackChannelId.trim() || null;

    if (this.connectorType === 'manual') {
      // Nothing to clone — the header on the processing page would otherwise
      // show a stuck "Pending" clone status forever with a zero-length list.
      scan.clone_job_status = 'COMPLETED';
      scan.repositories = [];
    } else {
      scan.repositories = this.repos
        .filter((r) => this.selectedRepos.has(r.fullName))
        .map((r) => ({
          repository_url: r.cloneUrl,
          name: r.name,
          full_name: r.fullName,
          private: r.isPrivate,
          status: 'PENDING',
          source: this.connectorType,
        }));
    }

    try {
      await scan.save();
      this.router.transitionTo('scans.scan.processing', scan.id as string);
    } catch {
      this.isStarting = false;
    }
  }
}
